import moment from "moment/moment.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import ModalBasic from "../../components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import { patchUser } from "../../api/admin.js";
import { addToast } from "../../store/app/action.js";
import { useDispatch, useSelector } from "react-redux";
import { forgotPassword } from "../../api/index.js";
import Badge from "../../components/Badges.jsx";

const userItem = ({
  user,
  storeID,
  refetch,
  setContextOpenedFor,
  contextOpenedFor,
  isAdmin,
}) => {
  const [showContext, setShowContext] = useState(false);
  const [warning, setWarning] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { user: currentUser, currentRole, selectedMerchant } = useSelector((state) => state.app);

  useEffect(() => {
    if (showContext) {
      setContextOpenedFor(user._id);
    }
  }, [showContext]);
  useEffect(() => {
    if (contextOpenedFor !== user._id) {
      setShowContext(false);
    }
  }, [contextOpenedFor]);
  let status = "Active";
  let statusColor = "text-emerald-500";
  if (
    user.invitation_expire &&
    moment(user.invitation_expire).isBefore(moment())
  ) {
    status = "Invitation Expired";
    statusColor = "text-yellow-500";
  } else if (user.invitation_expire) {
    status = "Invited";
    statusColor = "text-blue-500";
  }
  if (user.disabled) {
    status = "Disabled";
    statusColor = "text-red-500";
  }

  const permissions = useMemo(() => {
    let permissions = [];
    user.permissions.forEach((p) => {
      p.locations?.forEach((l) => {
        if (l.location === storeID) {
          permissions.push(...l.permissions);
        }
      });
    });
    return permissions;
  }, [user]);

  const callPatch = async (payload, successMessage, callOverride = null) => {
    setLoading(true);
    try {
      if (!callOverride) {
        await patchUser(user._id, payload);
      } else {
        await callOverride;
      }
      dispatch(
        addToast({
          type: "success",
          message: successMessage,
          timer: 5,
        })
      );
      setWarning("");
      setShowContext(false);
      setPendingAction(null);
      refetch();
    } catch (e) {
      let message = "An error occurred";
      try {
        const serverMessage = e.response?.data?.error;
        if (serverMessage) {
          message = serverMessage;
        }
      } catch {}
      message += ", updates are NOT saved.";
      dispatch(
        addToast({
          type: "error",
          message: message,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const badgeColor = useMemo(() => {
    if (!permissions) {
      return "";
    }
    if (permissions.includes("admin")) {
      return "red";
    } else if (permissions.includes("manager")) {
      return "yellow";
    } else {
      return "gray";
    }
  }, [permissions]);
  const resendInvitation = async () => {
    await callPatch({ op: "resend_invitation", direct: selectedMerchant.direct }, "Invitation resent");
  };
  const disableUser = async () => {
    await callPatch({ op: "disable" }, "User disabled");
  };
  const enableUser = async () => {
    await callPatch({ op: "enable" }, "User enabled");
  };
  const resetPassword = async () => {
    await callPatch(
      null,
      "Password reset email sent",
      forgotPassword(user.email)
    );
  };

  const actions = useMemo(() => {
    if (currentUser.type !== "saucey_admin") {
      if (!currentRole.includes("admin") && permissions.includes("admin")) {
        return [];
      }
    }
    const actions = [];
    if (user.invitation_expire && !user.disabled) {
      actions.push({
        name: "Resend Invitation",
        warning: "Are you sure you want to re-send an invitation?",
        action: resendInvitation,
      });
    }
    if (user.disabled) {
      actions.push({
        name: "Enable User",
        warning: "Are you sure you want to enable this user?",
        action: enableUser,
      });
    } else {
      actions.push({
        name: "Disable User",
        warning: "Are you sure you want to disable this user?",
        action: disableUser,
      });
    }

    if (!user.invitation_expire && !user.disabled) {
      actions.push({
        name: "Reset Password",
        warning: "Are you sure you want send a password reset email?",
        action: resetPassword,
      });
    }
    return actions;
  }, [user]);

  return (
    <tr key={user._id}>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="flex items-center">
          <div className="font-medium text-slate-800 dark:text-slate-100">
            {user.first_name ?? ""} {user.last_name ?? ""}{" "}
            <ModalBasic
              title={"Are you sure?"}
              width="sm"
              modalOpen={!!warning}
              close={() => setWarning("")}
            >
              <div>
                <p className="text-slate-600 dark:text-slate-400">{warning}</p>
                <div className="mt-4 flex justify-end space-x-4">
                  <SecondaryButton
                    onClick={() => {
                      setWarning("");
                    }}
                    disabled={loading}
                    loading={loading}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    disabled={loading}
                    loading={loading}
                    onClick={() => {
                      pendingAction.action();
                    }}
                  >
                    Confirm
                  </PrimaryButton>
                </div>
              </div>
            </ModalBasic>
          </div>
        </div>
      </td>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-left">{user.email}</div>
      </td>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className={`text-left ${statusColor}`}>{status}</div>
      </td>
      {!isAdmin && (
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="text-left">
            {permissions.map((p) => (
              <Badge color={badgeColor}>
                <div key={p} className="capitalize">
                  {p}
                </div>
              </Badge>
            ))}
          </div>
        </td>
      )}
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
        {/* Menu button */}
        {actions.length > 0 && (
          <button
            onClick={() => {
              setShowContext(!showContext);
            }}
            className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400 rounded-full"
          >
            <span className="sr-only">Menu</span>
            <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
              <circle cx="16" cy="16" r="2" />
              <circle cx="10" cy="16" r="2" />
              <circle cx="22" cy="16" r="2" />
            </svg>
          </button>
        )}
        <div
          className={`absolute right-0 mt-2 w-48 py-2 z-10 bg-white dark:bg-theme-surface-accent rounded-sm shadow-lg border border-slate-200 dark:border-slate-700 ${
            showContext ? "block" : "hidden"
          }`}
        >
          {actions.map((a) => (
            <span
              key={a.name}
              onClick={() => {
                setShowContext(false);
                setWarning(a.warning);
                setPendingAction(a);
              }}
              className="block px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
            >
              {a.name}
            </span>
          ))}
        </div>
      </td>
    </tr>
  );
};

export default userItem;
